import { defineMessages, useIntl } from 'react-intl'
import { Stack, Tab, TabList, Tabs } from '@homebotapp/hb-react-component-catalog'
import { Listing } from '../../../../api/gqlaxy/gql/generated/graphql'
import { useEffect, useState } from 'react'
import { HomeSearchTrackingEventLocation } from '../../../../constants/tracking'
import * as serializers from '../../../../charts/chart_serializer.js'
import BarChart from '../../../../charts/bar-chart'
import styles from './ShortTermRentalGraph.module.scss'

interface CalculatedData {
  rentalNightMax?: number
  monthlyRevenue?: number
  monthlyEarnings?: number
  annualEarnings?: number
  paymentMax?: number
}

interface ShortTermRentalGraphProps {
  listing: Listing
  data: any
  calculatedData: CalculatedData
}
const MONTHLY_REVENUE = 'monthlyRevenue'
const NIGHTLY_RATE = 'nightlyRate'
const MONTHLY_OCCUPANCY = 'monthlyOccupancy'

export const MSG = defineMessages({
  monthlyRevenue: {
    id: 'ShortTermRentalGraph.monthlyRevenue',
    defaultMessage: 'MONTHLY REVENUE'
  },
  nightlyRate: {
    id: 'ShortTermRentalGraph.nightlyRate',
    defaultMessage: 'NIGHTLY RATE'
  },
  nights: {
    id: 'ShortTermRentalGraph.nights',
    defaultMessage: '# OF NIGHTS'
  }
})

const TABS = [
  {
    key: MONTHLY_REVENUE
  },
  {
    key: NIGHTLY_RATE
  },
  {
    key: MONTHLY_OCCUPANCY
  }
]

export const ShortTermRentalGraph: React.FC<ShortTermRentalGraphProps> = ({ data, calculatedData }) => {
  const intl = useIntl()

  const [selectedTab, setSelectedTab] = useState('monthlyRevenue')

  const handleTabChange = (key: string) => {
    setSelectedTab(key)
  }

  useEffect(() => {
    const chartSvg = document.getElementById('air-dna-chart-svg')

    // This logic matches the logic in AirDnaChart.tsx
    const percentage = selectedTab === MONTHLY_REVENUE ? calculatedData?.monthlyRevenue || 1 : 1

    const serialize = serializers[selectedTab]
    let chart: BarChart

    if (chartSvg && data && percentage && serialize) {
      chart = new BarChart(chartSvg)

      const airDNAData = serialize(intl, data.stats.future.metrics, percentage)

      chart.update(airDNAData, 'en')
    }

    return () => chart?.destroy()
  }, [data, selectedTab, calculatedData])

  return (
    <Stack gap={0} justifyContent='center' alignItems='center'>
      <Tabs index={TABS.findIndex(tab => tab.key === selectedTab)} size='sm'>
        <TabList>
          <Tab
            onClick={() => handleTabChange('monthlyRevenue')}
            tracking={{
              guid: 'crY9YwBH6qdXwHbLZhJ3Li',
              descriptor: 'Monthly Revenue',
              ui_context:
                HomeSearchTrackingEventLocation.HOME_SEARCH_LISTING_DETAILS_SHORT_TERM_RENTAL_MONTHLY_REVENUE_TAB
            }}
          >
            {intl.formatMessage(MSG.monthlyRevenue)}
          </Tab>
          <Tab
            onClick={() => handleTabChange('nightlyRate')}
            tracking={{
              guid: 'i8VN8fta4qnneRo5HHCbLd',
              descriptor: 'Nightly Rate',
              ui_context: HomeSearchTrackingEventLocation.HOME_SEARCH_LISTING_DETAILS_SHORT_TERM_RENTAL_NIGHTLY_RATE_TAB
            }}
          >
            {intl.formatMessage(MSG.nightlyRate)}
          </Tab>
          <Tab
            onClick={() => handleTabChange('monthlyOccupancy')}
            tracking={{
              guid: 'jytWgPMMtQ7W6YUjkk8iW6',
              descriptor: '# of Nights',
              ui_context: HomeSearchTrackingEventLocation.HOME_SEARCH_LISTING_DETAILS_SHORT_TERM_RENTAL_NIGHTS_TAB
            }}
          >
            {intl.formatMessage(MSG.nights)}
          </Tab>
        </TabList>
      </Tabs>
      <div id='chart-container' className={`chart-container chart-container--bar-chart ${styles.chart}`}>
        <svg id='air-dna-chart-svg' className='bar-chart' />
      </div>
    </Stack>
  )
}
